import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CreateGalleryPresetBody, GalleryPresetDto, UpdateGalleryPresetBody } from '@picflow/types';
import { lastValueFrom } from 'rxjs';
import { StandardGalleryPreset, standardPresets } from '../configs/standard-presets';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';

@Injectable({
  providedIn: 'root',
})
export class GalleryPresetService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(DataLoaderService);

  private async getAllGalleryPresets() {
    return await this.loader.get('gallery_presets', () => this.getGalleryPresetsInternal());
  }

  async getCustomGalleryPresets(): Promise<GalleryPresetDto[]> {
    const resp = await this.getAllGalleryPresets();
    const presets = [...resp.filter(p => p.type === 'custom').sort((a, b) => (a.created_at > b.created_at ? 1 : -1))];
    return presets;
  }

  async getStandardGalleryPresets(): Promise<StandardGalleryPreset[]> {
    return Promise.all(
      standardPresets.map(async preset => {
        const backendPreset = await this.getBackendPresetForStandardPreset(preset.id);
        if (backendPreset) {
          preset.visible = backendPreset.visible;
          preset.is_default = backendPreset.is_default;
        }
        return preset;
      })
    );
  }

  async getDefaultPreset(): Promise<GalleryPresetDto | StandardGalleryPreset> {
    const resp = await this.getAllGalleryPresets();
    const preset = resp.find(p => p.is_default);

    if (preset.type === 'custom') {
      return preset;
    }

    const id = preset.name.toLocaleLowerCase().replace(' ', '-');
    return standardPresets.find(p => p.id === id);
  }

  async getGalleryPresetsInternal(): Promise<GalleryPresetDto[]> {
    const resp = await lastValueFrom(
      this.http.get<{ data: GalleryPresetDto[] }>(`${this.config.apiEndpoint}/gallery_presets`)
    );
    return resp.data;
  }

  async createPreset(preset: CreateGalleryPresetBody) {
    const resp = await lastValueFrom(
      this.http.post<GalleryPresetDto | StandardGalleryPreset>(`${this.config.apiEndpoint}/gallery_presets`, preset)
    );
    this.loader.remove('gallery_presets');
    return resp;
  }

  async deletePreset(id: string) {
    await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/gallery_presets/${id}`));
    this.loader.remove('gallery_presets');
  }

  private async getBackendPresetForStandardPreset(id: string) {
    const standardPreset = standardPresets.find(p => p.id === id);
    if (standardPreset) {
      const backendPresets = await this.getAllGalleryPresets();
      return backendPresets.find(p => p.name === standardPreset.backend_name);
    }
  }

  async updatePreset(id: string, preset: UpdateGalleryPresetBody) {
    const presetFix = await this.getBackendPresetForStandardPreset(id);
    if (presetFix) id = presetFix.id;

    await lastValueFrom(
      this.http.patch<GalleryPresetDto | StandardGalleryPreset>(
        `${this.config.apiEndpoint}/gallery_presets/${id}`,
        preset
      )
    );
    this.loader.remove('gallery_presets');
  }

  async makeDefault(id: string) {
    const presets = await this.getAllGalleryPresets();
    let backendPreset: GalleryPresetDto = presets.find(p => p.id === id);

    if (!backendPreset) {
      backendPreset = await this.getBackendPresetForStandardPreset(id);
    }

    await this.updatePreset(backendPreset.id, { is_default: true });
  }
}
